import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { interviewerService, slotService } from '../services';
import disclaimerIcon from "../assets/icons/copy/disclaimer icon.svg";
import userIcon from "../assets/icons/user.svg";
import welcomeBlockImage from "../assets/images/welcome block.png";

const RemoteCallBooking = () => {
  const navigate = useNavigate();
  const [selectedInterviewer, setSelectedInterviewer] = useState('');
  const [interviewers, setInterviewers] = useState([]);
  const [loadingInterviewers, setLoadingInterviewers] = useState(true);
  const [loadingNextStep, setLoadingNextStep] = useState(false);

  // Fetch public interviewers on component mount
  useEffect(() => {
    const fetchInterviewers = async () => {
      try {
        setLoadingInterviewers(true);
        const response = await interviewerService.showPublicInterviewers();
        console.log('Public interviewers response:', response);
        setInterviewers(response.interviewers || []);
      } catch (error) {
        console.error('Error fetching public interviewers:', error);
        setInterviewers([]);
      } finally {
        setLoadingInterviewers(false);
      }
    };

    fetchInterviewers();
  }, []);

  const handleNextStep = async () => {
    if (selectedInterviewer) {
      try {
        setLoadingNextStep(true);
        
      // Find the selected interviewer object to get complete details
      const selectedInterviewerData = interviewers.find(interviewer => interviewer.id === selectedInterviewer);
      
        // Fetch slots for the selected interviewer
        console.log('Fetching slots for interviewer:', selectedInterviewer);
        const slotsResponse = await slotService.getSlotsByInterviewer(selectedInterviewer);
        
        // Store the selections and slots data for the next component
      localStorage.setItem('remoteCallSelections', JSON.stringify({
        selectedInterviewer: selectedInterviewerData, // Store complete interviewer object
          slotsData: slotsResponse // Store the slots data for availability checking
        }));
        
        navigate('/assisted-recording');
      } catch (error) {
        console.error('Error fetching slots for interviewer:', error);
        // Navigate anyway but without slots data - AssistedRecording will handle the fallback
        const selectedInterviewerData = interviewers.find(interviewer => interviewer.id === selectedInterviewer);
        localStorage.setItem('remoteCallSelections', JSON.stringify({
          selectedInterviewer: selectedInterviewerData,
          slotsData: null
      }));
      navigate('/assisted-recording');
      } finally {
        setLoadingNextStep(false);
      }
    }
  };

  const isFormValid = selectedInterviewer;

  if (loadingInterviewers) {
    return (
      <div className="min-h-screen bg-background pt-9 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-foreground">Loading interviewers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-9">
      {/* Main Content */}
      <div className="max-w-2xl mx-auto px-4">
        {/* Video Call Interface */}
        <div className="bg-black rounded-xl overflow-hidden mb-6 relative">
          <img 
            src={welcomeBlockImage} 
            alt="Video call interface - Let's meet online with Marius Ciortan"
            className="w-full h-auto object-contain"
          />
        </div>

        {/* Meeting Info Section */}
        <div className="bg-secondary border border-secondary-border rounded-lg p-5 mb-6">
          <div className="flex items-center gap-3 mb-3">
            <div>
            <img src={disclaimerIcon} alt="Disclaimer" className="w-full h-full" />
            </div>
            <p className="text-base font-semibold text-foreground">You&apos;ve chosen to meet online.</p>
          </div>
          <p className="text-foreground text-base font-normal">
            Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed sollicitudin, mi et 
            mattis venenatis, turpis metus ornare erat, vestibulum mollis mi enim eu dolor.
          </p>
        </div>

        {/* Interviewer Selection */}
        <div className="mb-6">
          <h3 className="text-base font-semibold text-foreground mb-4">Choose your interviewer:</h3>
          {interviewers.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>No interviewers available at the moment.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {interviewers.map((interviewer) => (
                <button
                  key={interviewer.id}
                  onClick={() => setSelectedInterviewer(interviewer.id)}
                  className={`p-4 rounded-[8px] border-2 transition-all ${
                    selectedInterviewer === interviewer.id
                      ? 'border-primary bg-primary/10'
                      : 'border-secondary-border hover:border-primary/50 bg-secondary'
                  }`}
                >
                  <div className="w-16 h-16 bg-background rounded-full mx-auto mb-3 overflow-hidden flex items-center justify-center">
                    {interviewer.image ? (
                      <img 
                        src={interviewer.image} 
                        alt={interviewer.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <img 
                        src={userIcon} 
                        alt={interviewer.name}
                        className="w-8 h-8 object-contain"
                      />
                    )}
                  </div>
                  <p className="text-sm font-medium text-foreground">
                    Meet with {interviewer.name}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Next Step Button */}
        <button
          onClick={handleNextStep}
          disabled={!isFormValid || loadingNextStep}
          className={`w-full py-4 mb-4 rounded-lg text-base font-semibold transition-colors flex items-center justify-center ${
            isFormValid && !loadingNextStep
              ? 'bg-primary border border-primary-border text-primary-foreground hover:bg-primary/90'
              : 'bg-muted text-muted-foreground cursor-not-allowed opacity-50'
          }`}
        >
          {loadingNextStep ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              Loading...
            </>
          ) : (
            <>
          Next Step
          <svg className="w-5 h-5 ml-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 6L15 12L9 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default RemoteCallBooking; 